import { models } from "types/api/viewModels.ts";
import { AcknowledgementPriceFormat, AcknowledgementPriceFormatType } from "types/declarations/enums.ts";
import { ExtendedListPrice, ExtendedUnitPrice } from "constants/text.ts";
import { useSelector } from "react-redux";
import { RootState } from "stores/application.store.tsx";
import { useGetPermissionsQuery } from "features/api/permissionsApi.ts";
import { useMemo } from "react";

interface PriceLabelValue {
	priceLabel: string;
	priceValue: number | undefined | null;
}

export const useOrderDetailsLineItemGrid = (
	item: models["LineItemViewModel"],
	billTo?: models["BillToViewModel"] | null,
	ackPermissionOverride?: AcknowledgementPriceFormatType | null
) => {
	const { data: permissionsData } = useGetPermissionsQuery();
	const cartSelections = useSelector((state: RootState) => state.cart?.replacements);

	const { pricing } = item;
	const itemPricing = "pricing" in item ? item.pricing : item;
	const ackPermission = ackPermissionOverride ?? permissionsData?.acknowledgementPriceFormat;

	const isPartAdded = useMemo(
		() => Boolean(cartSelections?.find((cartItem: { id: string }) => item.id === cartItem.id)),
		[cartSelections, item.id]
	);

	const extendedPrice = useMemo((): PriceLabelValue | undefined => {
		const isListPermission = ackPermission === AcknowledgementPriceFormat.LIST && !billTo?.cashInAdvance;

		if (
			((itemPricing?.extendedNetPrice !== undefined && itemPricing.extendedNetPrice !== null) ||
				(pricing?.extendedAdjustedNetPrice !== undefined && pricing?.extendedAdjustedNetPrice !== null)) &&
			(!isListPermission || !("pricing" in item))
		) {
			return {
				priceLabel: ExtendedUnitPrice,
				priceValue:
					"pricing" in item
						? (item.pricing?.extendedAdjustedNetPrice ?? item.pricing?.extendedNetPrice)
						: itemPricing?.extendedNetPrice
			};
		}
		if (
			((pricing?.extendedListPrice !== undefined && pricing.extendedListPrice !== null) ||
				(pricing?.extendedAdjustedListPrice !== null && pricing?.extendedAdjustedListPrice !== undefined)) &&
			isListPermission
		) {
			return {
				priceLabel: ExtendedListPrice,
				priceValue: pricing.extendedAdjustedListPrice ?? pricing.extendedListPrice
			};
		}
		return undefined;
	}, [
		ackPermission,
		billTo?.cashInAdvance,
		itemPricing?.extendedNetPrice,
		pricing?.extendedAdjustedNetPrice,
		pricing?.extendedListPrice,
		pricing?.extendedAdjustedListPrice,
		item
	]);

	return { isPartAdded, extendedPrice };
};
